import { useTranslation } from 'react-i18next';
import type { components } from '@/types/api.generated';
import { CollapsibleCheck } from '../components/CollapsibleCheck';
import { DispatchOverviewSection } from '../components/DispatchOverviewSection';
import { DispatchRunCard, type DispatchRunCardProps } from '../components/DispatchRunCard';
import { PreDispatchChecks } from '../components/PreDispatchChecks';
import { UndatedOrdersSection } from '../components/UndatedOrdersSection';

type DispatchPreview = components['schemas']['DispatchPreview'];
type SalesBridge = components['schemas']['SalesBridge'];

export interface PendingPaneProps {
  date: string;
  /** The run card (the full-width bar on top), passed through. */
  runCard: Omit<DispatchRunCardProps, 'date'>;
  // The preview summary.
  total: number;
  statusCounts: DispatchPreview['statusCounts'] | undefined;
  /** `undefined` while the preview has not answered. */
  anomalies: DispatchPreview['anomalies'] | undefined;
  previewError: boolean;
  salesBridge: SalesBridge | undefined;
  salesBridgeError: boolean;
  /** Listed only while the switch is on (#2433). */
  stopAlreadyDispatched: DispatchPreview['stopAlreadyDispatched'];
  previewLoading: boolean;
  reviewLoading: boolean;
  totalReviewItems: number;
}

/**
 * The awaiting-dispatch pane, stacked top to bottom: the run card as a
 * full-width bar, the pre-dispatch checks, then the older overview (KPIs, funnel, count check) folded into "more checks"
 * (#2475, epic #2469 C.2). The queries and the run mutation stay on the page
 * so switching cells loses no progress.
 */
export function PendingPane({
  date,
  runCard,
  total,
  statusCounts,
  anomalies,
  previewError,
  salesBridge,
  salesBridgeError,
  stopAlreadyDispatched,
  previewLoading,
  reviewLoading,
  totalReviewItems,
}: PendingPaneProps) {
  const { t } = useTranslation();
  return (
    <div data-testid="dispatch-pane-awaiting_dispatch" className="space-y-4">
      <DispatchRunCard date={date} {...runCard} />
      <UndatedOrdersSection date={date} />
      <PreDispatchChecks
        date={date}
        anomalies={anomalies}
        previewError={previewError}
        salesBridge={salesBridge}
        salesBridgeError={salesBridgeError}
      />
      <CollapsibleCheck
        testId="dispatch-more-checks"
        title={t('dispatch.moreChecks.title')}
        badge={null}
        defaultOpen={false}
      >
        <DispatchOverviewSection
          date={date}
          dispatchable={runCard.tasks}
          total={total}
          statusCounts={statusCounts}
          stopAlreadyDispatched={stopAlreadyDispatched}
          previewLoading={previewLoading}
          reviewLoading={reviewLoading}
          totalReviewItems={totalReviewItems}
        />
      </CollapsibleCheck>
    </div>
  );
}
